import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../common/Button.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';
import PaidAmountSettleModal from '../paid-amount-settlement/PaidAmountSettleModal.js';
import { statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const MlPaidSettlementGate = ({
  mlCreationBlock = {},
  dues,
  agentDues = [],
  show = true,
  hardBlocked = false,
  settleOpen,
  setSettleOpen,
  onSettleSubmitted,
  settlementPath = '/paid-amount-settlements',
  allowSettle = true,
  variant = 'full',
  collapsible = false,
  defaultExpanded = false,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(() => (collapsible ? defaultExpanded : true));

  if (!show) return null;

  const pendingStatus = mlCreationBlock?.pending_request_status;
  const canSettle = Boolean(
    allowSettle
    && dues
    && !dues.pending_request_id
    && (Number(dues.amount_outstanding || 0) > 0),
  );
  const isCompact = variant === 'compact';
  const warningOnly = !hardBlocked && !pendingStatus;
  const hubAgentRows = (Array.isArray(agentDues) ? agentDues : []).filter(
    (row) => Number(row.amount_outstanding || 0) > 0 || row.pending_request_id,
  );

  const openSettleModal = () => {
    if (!canSettle) return;
    setSettleOpen(true);
  };

  const toggleExpanded = () => {
    if (collapsible) setExpanded((prev) => !prev);
  };

  const titleText = pendingStatus
    ? 'Paid settlement in progress'
    : warningOnly
      ? 'Unremitted paid collections'
      : 'Remit paid collections before creating ML';

  const bodyExpanded = !collapsible || expanded;

  return (
    <>
      <div
        className={`rounded-xl border ${
          pendingStatus === 'OCR_PENDING'
            ? 'border-blue-200 bg-blue-50'
            : warningOnly
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
        } px-3 sm:px-4 py-3 ${className}`}
      >
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={toggleExpanded}
            disabled={!collapsible}
            className={`min-w-0 flex-1 text-left ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
            aria-expanded={collapsible ? expanded : undefined}
          >
            <div className="flex items-start gap-2">
              {pendingStatus === 'OCR_PENDING' ? (
                <Loader2 className="w-4 h-4 mt-0.5 shrink-0 text-blue-700 animate-spin" />
              ) : (
                <Wallet className={`w-4 h-4 mt-0.5 shrink-0 ${warningOnly ? 'text-amber-700' : 'text-red-700'}`} />
              )}
              <div className="min-w-0">
                <p
                  className={`text-sm font-semibold ${
                    pendingStatus === 'OCR_PENDING'
                      ? 'text-blue-900'
                      : warningOnly
                        ? 'text-amber-900'
                        : 'text-red-900'
                  }`}
                >
                  {titleText}
                </p>
                {collapsible && !expanded && Number(mlCreationBlock?.amount_outstanding || 0) > 0 && (
                  <p
                    className={`text-xs mt-0.5 tabular-nums ${
                      warningOnly ? 'text-amber-800' : 'text-red-800'
                    }`}
                  >
                    Outstanding {fullCurrency(mlCreationBlock.amount_outstanding)}
                    {collapsible ? ' — click to expand' : ''}
                  </p>
                )}
              </div>
            </div>
          </button>
          <div className="flex items-center gap-1.5 shrink-0">
            {collapsible && !expanded && canSettle && (
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openSettleModal();
                }}
              >
                Settle now
              </Button>
            )}
            {collapsible && (
              <button
                type="button"
                onClick={toggleExpanded}
                className="p-1.5 rounded-lg text-gray-600 hover:bg-black/5"
                aria-label={expanded ? 'Collapse settlement alert' : 'Expand settlement alert'}
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        <div
          className={`${
            bodyExpanded ? 'block' : 'hidden'
          } ${
            collapsible
              ? 'mt-2 max-h-[min(240px,38vh)] md:max-h-[min(300px,36vh)] lg:max-h-[min(340px,32vh)] overflow-y-auto overscroll-contain -mx-1 px-1'
              : ''
          }`}
        >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="min-w-0">
                <p
                  className={`text-xs sm:text-sm ${
                    pendingStatus === 'OCR_PENDING'
                      ? 'text-blue-800'
                      : warningOnly
                        ? 'text-amber-900'
                        : 'text-red-800'
                  }`}
                >
                  {mlCreationBlock?.reason}
                </p>
                {warningOnly && (
                  <p className="text-xs sm:text-sm mt-1.5 text-amber-900">
                    You can still create an ML with <span className="font-semibold">To Pay</span> shipments.
                    Paid shipments with unremitted cash cannot be added until settled.
                  </p>
                )}
                {!pendingStatus && Number(mlCreationBlock?.amount_outstanding || 0) > 0 && (
                  <p
                    className={`text-xs sm:text-sm mt-1.5 ${
                      warningOnly ? 'text-amber-900' : 'text-red-900'
                    }`}
                  >
                    Outstanding:{' '}
                    <span className="font-semibold tabular-nums">
                      {fullCurrency(mlCreationBlock.amount_outstanding)}
                    </span>
                  </p>
                )}
                {pendingStatus && (
                  <p className="text-xs mt-1.5 text-blue-700">
                    You can prepare your ML sheet below. Creation unlocks once the settlement is approved.
                  </p>
                )}
            </div>

            {!isCompact && hubAgentRows.length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-amber-100 bg-white/70">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Agent</th>
                      <th className="text-right px-3 py-2 font-semibold">Outstanding</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {hubAgentRows.slice(0, 6).map((row) => (
                      <tr key={row.agent_user_id}>
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {row.agent_name || row.agent_user_id}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">
                          {fullCurrency(row.amount_outstanding || 0)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {row.pending_request_id
                            ? `${statusLabel(row.pending_request_status || 'PENDING')} (${row.pending_request_id})`
                            : `${row.shipment_count || row.shipments?.length || 0} shipment(s)`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hubAgentRows.length > 6 && (
                  <p className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100">
                    +{hubAgentRows.length - 6} more agent(s)
                  </p>
                )}
              </div>
            )}

            {!isCompact && hubAgentRows.length === 0 && dues?.shipments?.length > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-red-100 bg-white/70">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Shipment</th>
                      <th className="text-left px-3 py-2 font-semibold">Date</th>
                      <th className="text-right px-3 py-2 font-semibold">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dues.shipments.slice(0, 5).map((row) => (
                      <tr key={row.shipment_id}>
                        <td className="px-3 py-2 font-medium text-gray-800">{row.shipment_id}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {formatIndianDate(row.booking_date)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">
                          {fullCurrency(row.amount_outstanding)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {dues.shipments.length > 5 && (
                  <p className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100">
                    +{dues.shipments.length - 5} more shipment(s)
                  </p>
                )}
              </div>
            )}

            {dues?.pending_request_id && (
              <p className="text-xs mt-2 text-amber-800">
                Request {dues.pending_request_id} — {statusLabel(dues.pending_request_status || 'PENDING')}
                {dues.pending_request_status === 'HUMAN_REVIEW' ? ' (awaiting accountant)' : ''}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            {canSettle && (
              <Button variant="primary" size="sm" onClick={openSettleModal}>
                Settle now
              </Button>
            )}
            <Link
              to={settlementPath}
              className="inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              {allowSettle ? 'Full settlement page' : 'Settlement approvals'}
            </Link>
          </div>
        </div>
        </div>
      </div>

      {settleOpen && dues && allowSettle && (
        <PaidAmountSettleModal
          dues={dues}
          onClose={() => setSettleOpen(false)}
          onSubmitted={onSettleSubmitted}
        />
      )}
    </>
  );
};

export default MlPaidSettlementGate;
